import { css } from '@compiled/react';

import { media } from '@atlaskit/primitives/responsive';

import { wacToken } from '@/libraries/design/foundations';

import type { FacetPanelProps } from '../types';

/**
 * Always-visible checkbox facet lists (reference parity — no dropdown, no
 * staged phase). Two 584px columns on desktop, one stacked column below `sm`.
 * The native input is visually hidden (not `display:none`, so it stays
 * keyboard-focusable) — the sibling `<span>` is the visible box and reflects
 * both checked and `:focus-visible` state.
 */
export const FacetPanel = ({ groups, onToggleOption }: FacetPanelProps) => {
	return (
		<div css={panelStyle}>
			{groups.map((group) => (
				<fieldset key={group.id} css={fieldsetStyle}>
					<legend css={legendStyle}>{group.label}</legend>
					<ul css={listStyle}>
						{group.options.map((option) => (
							<li key={option.value} css={listItemStyle}>
								<label css={optionLabelStyle}>
									<span css={checkboxWrapperStyle}>
										<input
											type="checkbox"
											checked={option.checked}
											onChange={() => onToggleOption(group.id, option.value)}
											css={checkboxInputStyle}
										/>
										<span
											aria-hidden="true"
											css={[checkboxBoxStyle, option.checked && checkboxBoxCheckedStyle]}
										/>
									</span>
									{`${option.label} (${option.count})`}
								</label>
							</li>
						))}
					</ul>
				</fieldset>
			))}
		</div>
	);
};

const panelStyle = css({
	display: 'flex',
	flexDirection: 'column',
	gap: '24px',
	[media.above.sm]: {
		flexDirection: 'row',
		gap: 0,
	},
});

const fieldsetStyle = css({
	border: 0,
	margin: 0,
	padding: 0,
	minWidth: 0,
	flex: '1 1 0',
	[media.above.sm]: {
		flexBasis: '584px',
	},
});

const legendStyle = css({
	fontFamily: wacToken('font.charlie.display'),
	fontSize: '16px',
	lineHeight: '19.2px',
	fontWeight: 600,
	letterSpacing: '1px',
	textTransform: 'uppercase',
	color: wacToken('color.primary.blue'),
	marginBlockEnd: '24px',
	padding: 0,
});

const listStyle = css({
	listStyle: 'none',
	margin: 0,
	padding: 0,
	[media.above.sm]: {
		paddingInlineEnd: '50px',
	},
});

const listItemStyle = css({
	marginBlockEnd: '12px',
});

const optionLabelStyle = css({
	display: 'flex',
	alignItems: 'center',
	cursor: 'pointer',
	fontFamily: wacToken('font.charlie.text'),
	fontSize: '16px',
	lineHeight: '24px',
	fontWeight: 400,
	color: wacToken('color.primary.blue'),
});

const checkboxWrapperStyle = css({
	position: 'relative',
	width: '16px',
	height: '16px',
	marginInlineEnd: '8px',
	flexShrink: 0,
});

const checkboxInputStyle = css({
	position: 'absolute',
	insetBlockStart: 0,
	insetInlineStart: 0,
	width: '100%',
	height: '100%',
	margin: 0,
	opacity: 0,
	cursor: 'pointer',
	appearance: 'none',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'&:focus-visible ~ span': {
		outline: `2px solid ${wacToken('color.primary.blue')}`,
		outlineOffset: '2px',
	},
});

const checkboxBoxStyle = css({
	position: 'relative',
	// Required: a <span> is display:inline by default, and width/height do not
	// apply to inline elements. Without this the box collapses to a 2px-wide
	// border artifact instead of the 16x16 box, and the checked ::after
	// checkmark has no box to sit in. The wrapper span escapes this only
	// because the label is display:flex, which blockifies its children.
	display: 'block',
	width: '16px',
	height: '16px',
	border: `1px solid ${wacToken('color.primary.blue')}`,
	borderRadius: 0,
	backgroundColor: 'transparent',
});

const checkboxBoxCheckedStyle = css({
	backgroundColor: wacToken('color.primary.blue'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'&::after': {
		content: '""',
		position: 'absolute',
		insetInlineStart: '4px',
		insetBlockStart: '1px',
		width: '5px',
		height: '9px',
		border: `solid ${wacToken('color.neutral.light0')}`,
		borderWidth: '0 2px 2px 0',
		transform: 'rotate(45deg)',
	},
});
